import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import {
  boardPointToNormalized,
  categoriesText,
  containRect,
  imageFlip,
  normalizedToBoardPoint,
  statusLabel,
  turnPoint,
  type NormalizedPoint,
} from '@fsv/core';
import type { Damage } from '@fsv/data';
import type { ResolvedDrawing } from '../drawings/useDrawing';
import { tx } from '@fsv/data';
import { clusterOnScreen } from '@fsv/data';
import {
  boardToScreen,
  IDENTITY,
  LONG_PRESS_MS,
  LOUPE_SIZE,
  loupeFrame,
  loupeTransform,
  MAX_SCALE,
  panBy,
  pinch,
  placeAt,
  pressAfterHold,
  pressAfterMove,
  pressOnRelease,
  screenToBoard,
  wheelFactor,
  zoomAt,
  type BoxSize,
  type Point,
  type PressMode,
  type Transform,
} from '@fsv/data';
import { Icon } from './Icon';
import './DrawingBoard.css';

/**
 * The plan: a 3:2 board with the drawing laid in by object-fit: contain,
 * zoom/pan by pointer events (pinch, wheel, drag, keyboard), and pins drawn
 * OUTSIDE the zoomed layer so they keep their 44 px tap target.
 *
 * Coordinates: a tap is mapped screen → board (undo zoom) → image (undo
 * letterbox) with core's boardPointToNormalized. Mirrored views flip only the
 * image (scaleX(-1)); coordinates are in the on-screen frame of the view. Pins go the
 * other way with normalizedToBoardPoint. Nothing here knows the database.
 *
 * `turned` (somebody standing at the nose, core `isTurned`) shows the view by
 * 180°. Inside the board everything — the cross, the loupe, the pins — lives
 * in that turned screen frame; what comes in (`pins`, `provisional`) and what
 * goes out (`onTapEmpty`) is the stored, unturned frame, converted with
 * `turnPoint` at exactly those edges.
 *
 * One pointer: a tap or click places the crosshair, a drag pans in every
 * direction. Touch, where a report can be placed: a finger held still for
 * LONG_PRESS_MS brings up a loupe above it (the spot magnified, the cross in
 * the middle), the cross follows the finger and lands where it lifts; holding
 * on the cross picks it up with its offset. Two fingers zoom and pan.
 */
export function DrawingBoard({
  resolved,
  pins,
  label,
  selectedId,
  provisional,
  onTapEmpty,
  onPinTap,
  onClusterTap,
  watermark,
  credit,
  damageRef,
  turned = false,
}: {
  resolved: ResolvedDrawing;
  pins: Damage[];
  label: string;
  selectedId?: string | null | undefined;
  provisional?: NormalizedPoint | null | undefined;
  onTapEmpty?: ((point: NormalizedPoint) => void) | undefined;
  onPinTap: (damage: Damage) => void;
  onClusterTap: (damages: Damage[]) => void;
  watermark?: string | undefined;
  credit?: string | undefined;
  /** Damage number as the club writes it (FSV-000042), for the pins' accessible names. */
  damageRef: (n: number) => string;
  /** Show the view turned by 180° (the reporter stands at the nose). */
  turned?: boolean;
}) {
  const { drawing, mirrored } = resolved;
  const flip = imageFlip(mirrored, turned);
  const imageTransform = flip.scaleX === 1 && flip.scaleY === 1 ? undefined : `scale(${flip.scaleX}, ${flip.scaleY})`;
  // Stored frame ↔ this screen; a turn by 180° is its own inverse.
  const screenFrame = useCallback((p: NormalizedPoint): NormalizedPoint => turnPoint({ x: p.x, y: p.y }, turned), [turned]);
  const boardRef = useRef<HTMLDivElement>(null);
  const [board, setBoard] = useState<BoxSize>({ width: 0, height: 0 });
  const [t, setT] = useState<Transform>(IDENTITY);
  const tRef = useRef(t);
  tRef.current = t;

  useLayoutEffect(() => {
    const el = boardRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setBoard((b) => (b.width === r.width && b.height === r.height ? b : { width: r.width, height: r.height }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // A resize changes what "the same zoom" means; start from the whole drawing again.
  useEffect(() => setT(IDENTITY), [board.width, board.height]);

  const image = useMemo(() => ({ width: drawing.width, height: drawing.height }), [drawing.width, drawing.height]);
  const rect = board.width > 0 ? containRect(image, board) : null;

  // ---------------------------------------------------------------- pins
  const clusters = useMemo(() => {
    if (!rect) return [];
    const screenPins = pins.map((d) => {
      const bp = normalizedToBoardPoint(screenFrame(d), image, board);
      return { id: d.id, screen: boardToScreen(t, bp), item: d };
    });
    const visible = screenPins.filter((p) => p.screen.x >= -8 && p.screen.y >= -8 && p.screen.x <= board.width + 8 && p.screen.y <= board.height + 8);
    return clusterOnScreen(visible, board.width);
  }, [pins, image, board, t, rect, screenFrame]);

  // ---------------------------------------------------------------- placing by touch
  const [placing, setPlacing] = useState<{ finger: Point; point: NormalizedPoint } | null>(null);
  const grab = useRef<{ dx: number; dy: number; pointerId: number } | null>(null);
  const placeFrom = (finger: Point) => {
    const g = grab.current;
    return { finger, point: placeAt(tRef.current, { x: finger.x + (g?.dx ?? 0), y: finger.y + (g?.dy ?? 0) }, image, board) };
  };

  // `placing.point` is in the screen frame (the loupe shows it); `provisional` comes stored.
  const cross = placing?.point ?? (provisional ? screenFrame(provisional) : null);
  const provisionalScreen = cross && rect ? boardToScreen(t, normalizedToBoardPoint(cross, image, board)) : null;

  // ---------------------------------------------------------------- gestures
  const pointers = useRef(new Map<number, Point>());
  const gesture = useRef<{ start: Transform; from: [Point, Point] } | null>(null);
  // One pointer: tap places, drag pans, a touch held still brings the loupe (pressAfter* in @fsv/data).
  const press = useRef<{ mode: PressMode; start: Transform; origin: Point; last: Point; pointerId: number; timer: number | null } | null>(null);

  const local = (e: { clientX: number; clientY: number }): Point => {
    const r = boardRef.current?.getBoundingClientRect();
    return { x: e.clientX - (r?.left ?? 0), y: e.clientY - (r?.top ?? 0) };
  };

  const tapAt = useCallback(
    (screen: Point) => {
      if (!onTapEmpty || board.width === 0) return;
      const bp = screenToBoard(tRef.current, screen);
      const n = boardPointToNormalized(bp.x, bp.y, image, board);
      if (n) onTapEmpty(screenFrame(n));
    },
    [onTapEmpty, image, board, screenFrame],
  );

  const stopTimer = () => {
    if (press.current?.timer != null) window.clearTimeout(press.current.timer);
    if (press.current) press.current.timer = null;
  };
  useEffect(() => () => stopTimer(), []);

  const showLoupe = () => {
    const pr = press.current;
    if (!pr) return;
    pr.timer = null;
    pr.mode = pressAfterHold(pr.mode);
    if (pr.mode !== 'loupe') return;
    // Holding on the cross picks it up where it is; anywhere else the cross jumps under the finger.
    const at = provisional ? boardToScreen(tRef.current, normalizedToBoardPoint(screenFrame(provisional), image, board)) : null;
    const near = at !== null && Math.hypot(at.x - pr.last.x, at.y - pr.last.y) <= 22;
    grab.current = { dx: near ? at.x - pr.last.x : 0, dy: near ? at.y - pr.last.y : 0, pointerId: pr.pointerId };
    setPlacing(placeFrom(pr.last));
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('[data-pin],[data-board-ui]')) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = local(e);
    pointers.current.set(e.pointerId, p);
    if (pointers.current.size === 1) {
      stopTimer();
      const loupe = e.pointerType !== 'mouse' && onTapEmpty && rect;
      press.current = {
        mode: 'pending',
        start: tRef.current,
        origin: p,
        last: p,
        pointerId: e.pointerId,
        timer: loupe ? window.setTimeout(showLoupe, LONG_PRESS_MS) : null,
      };
    } else if (pointers.current.size === 2) {
      // A second finger means zoom: no tap, no loupe; the cross set before stays.
      stopTimer();
      press.current = null;
      grab.current = null;
      setPlacing(null);
      const [a, b] = [...pointers.current.values()] as [Point, Point];
      gesture.current = { start: tRef.current, from: [a, b] };
    }
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(e.pointerId)) return;
    const p = local(e);
    pointers.current.set(e.pointerId, p);
    if (gesture.current && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()] as [Point, Point];
      setT(pinch(gesture.current.start, gesture.current.from, [a, b], board));
      return;
    }
    const pr = press.current;
    if (!pr || pr.pointerId !== e.pointerId) return;
    pr.last = p;
    const mode = pressAfterMove(pr.mode, pr.origin, p);
    if (mode !== pr.mode) stopTimer();
    pr.mode = mode;
    if (mode === 'pan') setT(panBy(pr.start, p.x - pr.origin.x, p.y - pr.origin.y, board));
    else if (mode === 'loupe') setPlacing(placeFrom(p));
  };

  const endPointer = (e: PointerEvent<HTMLDivElement>, cancelled: boolean) => {
    if (!pointers.current.has(e.pointerId)) return;
    const p = local(e);
    pointers.current.delete(e.pointerId);
    const pr = press.current;
    if (pr && pr.pointerId === e.pointerId) {
      stopTimer();
      press.current = null;
      const outcome = cancelled ? 'none' : pressOnRelease(pr.mode);
      const done = outcome === 'loupe' && grab.current ? placeFrom(p).point : null;
      grab.current = null;
      setPlacing(null);
      if (done) onTapEmpty?.(screenFrame(done));
      else if (outcome === 'tap') tapAt(p);
    }
    if (pointers.current.size < 2) gesture.current = null;
    if (pointers.current.size === 1) {
      // Lifting one finger of a pinch continues as a pan with the other, never as a tap.
      const [[id, rest]] = [...pointers.current.entries()] as [[number, Point]];
      press.current = { mode: 'pan', start: tRef.current, origin: rest, last: rest, pointerId: id, timer: null };
    }
  };

  // Wheel needs a non-passive listener to stop the page from scrolling.
  useEffect(() => {
    const el = boardRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const focal = { x: e.clientX - r.left, y: e.clientY - r.top };
      const size = { width: r.width, height: r.height };
      setT((cur) => zoomAt(cur, wheelFactor(e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY), focal, size));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const center = { x: board.width / 2, y: board.height / 2 };
  const zoomBy = (factor: number) => setT((cur) => zoomAt(cur, factor, center, board));

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    const step = 48;
    const keys: Record<string, () => void> = {
      ArrowLeft: () => setT((c) => panBy(c, step, 0, board)),
      ArrowRight: () => setT((c) => panBy(c, -step, 0, board)),
      ArrowUp: () => setT((c) => panBy(c, 0, step, board)),
      ArrowDown: () => setT((c) => panBy(c, 0, -step, board)),
      '+': () => zoomBy(1.4),
      '=': () => zoomBy(1.4),
      '-': () => zoomBy(1 / 1.4),
      '0': () => setT(IDENTITY),
      Enter: () => tapAt(center),
      ' ': () => tapAt(center),
    };
    const action = keys[e.key];
    if (action) {
      e.preventDefault();
      action();
    }
  };

  const contentStyle = { transform: `translate(${t.x}px, ${t.y}px) scale(${t.scale})` };
  const hintId = `${label.replace(/\W+/g, '-')}-hint`;

  return (
    <div className="board-wrap" data-tour="plan-board">
      <div
        ref={boardRef}
        className={`board${onTapEmpty ? ' board--tappable' : ''}`}
        role="group"
        aria-label={label}
        aria-describedby={hintId}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={(e) => endPointer(e, false)}
        onPointerCancel={(e) => endPointer(e, true)}
        onKeyDown={onKeyDown}
      >
        <div className="board__content" style={contentStyle}>
          {rect && (
            <img
              className="board__image"
              src={drawing.url}
              alt=""
              draggable={false}
              style={{
                left: rect.left,
                top: rect.top,
                width: rect.width,
                height: rect.height,
                transform: imageTransform,
              }}
            />
          )}
        </div>
        {watermark && (
          <div className="board__watermark" aria-hidden="true">
            {watermark}
          </div>
        )}
        <div className="board__crosshair" aria-hidden="true" />
        {clusters.map((c) => {
          const first = c.members[0];
          if (!first) return null;
          const style = { left: c.center.x, top: c.center.y };
          if (c.members.length === 1) {
            const d = first.item;
            return (
              <button
                key={c.key}
                type="button"
                data-pin
                className={`pin st--${d.status}${d.id === selectedId ? ' is-selected' : ''}`}
                style={style}
                aria-label={tx.plan.pinLabel(damageRef(d.number), categoriesText(d.categories), statusLabel[d.status])}
                onClick={() => onPinTap(d)}
              >
                <span className="pin__dot" />
              </button>
            );
          }
          const items = c.members.map((m) => m.item);
          const lead = items.find((d) => d.status === 'gemeldet') ?? items.find((d) => d.status !== 'behoben') ?? first.item;
          return (
            <button
              key={c.key}
              type="button"
              data-pin
              className={`pin pin--cluster st--${lead.status}${items.some((d) => d.id === selectedId) ? ' is-selected' : ''}`}
              style={style}
              aria-label={tx.plan.clusterLabel(items.length)}
              onClick={() => onClusterTap(items)}
            >
              <span className="pin__count mono">{items.length}</span>
            </button>
          );
        })}
        {provisionalScreen && (
          <div className="pin pin--provisional" style={{ left: provisionalScreen.x, top: provisionalScreen.y }} aria-label={tx.plan.provisional} role="img">
            <span className="pin__dot" />
          </div>
        )}
        {placing && rect && <Loupe placing={placing} t={t} board={board} image={image} rect={rect} url={drawing.url} imageTransform={imageTransform} />}
        <div className="board__zoom" data-board-ui role="toolbar" aria-label={tx.plan.zoomReset}>
          <button type="button" className="board__zbtn" onClick={() => zoomBy(1.6)} disabled={t.scale >= MAX_SCALE} aria-label={tx.plan.zoomIn}>
            <Icon name="plus" />
          </button>
          <button type="button" className="board__zbtn" onClick={() => zoomBy(1 / 1.6)} disabled={t.scale <= 1} aria-label={tx.plan.zoomOut}>
            <Icon name="minus" />
          </button>
          <button type="button" className="board__zbtn" onClick={() => setT(IDENTITY)} disabled={t.scale === 1} aria-label={tx.plan.zoomReset}>
            <Icon name="fit" />
          </button>
        </div>
        {t.scale > 1.01 && (
          <div className="board__scale mono" aria-live="polite">
            {t.scale.toFixed(1).replace('.', ',')}×
          </div>
        )}
        {(credit || drawing.placeholder) && (
          <div className="board__credit">
            {drawing.placeholder ? tx.plan.placeholderDrawing : `${tx.plan.credit}: ${credit}`}
          </div>
        )}
      </div>
      <p id={hintId} className="sr-only">
        {tx.plan.keyboardHint}
      </p>
    </div>
  );
}

/** The magnified spot under the finger with the cross in its centre; the drawing only, no pins. */
function Loupe({
  placing,
  t,
  board,
  image,
  rect,
  url,
  imageTransform,
}: {
  placing: { finger: Point; point: NormalizedPoint };
  t: Transform;
  board: BoxSize;
  image: { width: number; height: number };
  rect: { left: number; top: number; width: number; height: number };
  url: string;
  /** The board's own flip of the image (mirror and/or turn), so the loupe shows what the board shows. */
  imageTransform: string | undefined;
}) {
  const frame = loupeFrame(placing.finger, board);
  const inner = loupeTransform(t, normalizedToBoardPoint(placing.point, image, board));
  return (
    <div className="board__loupe" data-testid="placement-loupe" aria-hidden="true" style={{ left: frame.left, top: frame.top, width: LOUPE_SIZE, height: LOUPE_SIZE }}>
      <div className="board__content" style={{ width: board.width, height: board.height, transform: `translate(${inner.x}px, ${inner.y}px) scale(${inner.scale})` }}>
        <img
          className="board__image"
          src={url}
          alt=""
          draggable={false}
          style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height, transform: imageTransform }}
        />
      </div>
      <span className="board__loupe-cross" />
    </div>
  );
}
